import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import PageHeader from '@/components/feature/PageHeader';
import ContributeCta from '@/components/feature/ContributeCta';
import { useSeo, siteOrigin } from '@/hooks/useSeo';
import { topics } from '@/mocks/topics';
import TopicsExplorer from './components/TopicsExplorer';

const notes = [
  {
    icon: 'ri-node-tree',
    title: '一个专题，来自多个频道',
    desc: '例如“品牌翻车”专题会同时收录财经频道的事件复盘、消费频道的用户反馈与科技频道的产品问题记录。',
  },
  {
    icon: 'ri-sort-desc',
    title: '按时间与信源整理',
    desc: '专题内的内容默认按时间倒序排列，标注发布时间与信源类型，方便你判断事件的推进节奏。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续补充与修订',
    desc: '当事件出现新进展或当事方回应时，会向已有专题追加内容，并在原文标注更新说明。',
  },
];

const topicsJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      name: '专题分类 | 爆料官网',
      url: `${siteOrigin}/topics`,
      description:
        '爆料官网专题分类页，按行业黑幕、品牌翻车、明星八卦、产品测评、维权案例、深度调查六大主题横向聚合全站内容。',
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteOrigin },
        { '@type': 'ListItem', position: 2, name: '专题分类', item: `${siteOrigin}/topics` },
      ],
    },
    {
      '@type': 'ItemList',
      name: '爆料官网专题分类',
      numberOfItems: topics.length,
      itemListElement: topics.map((topic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: topic.name,
        description: topic.desc,
        url: `${siteOrigin}/topics#topic-${topic.slug}`,
      })),
    },
  ],
};

export default function Topics() {
  useSeo({
    title: '专题分类 | 爆料官网主题聚合与选题方向',
    description:
      '爆料官网专题分类页，按行业黑幕、品牌翻车、明星八卦、产品测评、维权案例与深度调查六大主题横向聚合内容，说明专题的组织方式与选题方向。',
    keywords: '专题分类,爆料官网专题,主题聚合,选题方向,内容专题,横向阅读',
    path: '/topics',
    jsonLd: topicsJsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          eyebrow="专题分类"
          title="按主题横向聚合，把一条线索读完整"
          description="同一件事常分散在不同频道的报道里。专题分类把它们按主题重新编排，让你不必来回切换，就能看到完整脉络。"
          crumbs={[{ label: '专题分类' }]}
          meta={[
            { label: '专题方向', value: '6 类' },
            { label: '覆盖频道', value: '10 个' },
            { label: '持续更新', value: '每日常规' },
          ]}
        />

        <TopicsExplorer />

        <section id="topic-notes" className="scroll-mt-28 bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">专题是怎么组织出来的</h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                专题不是简单地把文章叠在一起，而是按照主题边界、时间顺序与信源类型重新编排，让阅读路径更顺。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {notes.map((note) => (
                <div key={note.title} className="flex flex-col gap-3 rounded-lg border border-background-300/60 bg-background-50 p-5 md:p-6">
                  <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
                    <i className={`${note.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{note.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-600">{note.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {topics.map((topic) => (
                <a
                  key={topic.slug}
                  id={`topic-${topic.slug}`}
                  href="#topic-grid"
                  className="scroll-mt-28 inline-flex items-center gap-2 rounded-full border border-background-300/60 bg-background-50 px-4 py-2 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                >
                  <i className={`${topic.icon} text-primary-500`} aria-hidden="true" />
                  {topic.name}
                  <span className="text-xs text-foreground-400">{topic.count}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <ContributeCta
          title="你有完整的事件线索引？我们可以做成一期专题"
          desc="如果你掌握同一话题的多条线索或长期记录，欢迎整体提交。编辑部会评估是否适合做成独立专题。"
        />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}